// src/client/ui/screens/shape-palette.ts
//
// Shape-icon palette — canonical spec §9 mapping (lines 615-630).
//
// ⚠️  DISCREPANCY NOTICE: character-select.ts:88-99 contains a DIFFERENT
//     (incorrect) glyph ordering that was authored before spec §9 was finalised.
//     That mapping assigns, e.g. red→▲ (triangle) and blue→● (circle), which is
//     the reverse of the spec §9 canonical table.  Do NOT use character-select.ts
//     as a reference for this module.  The authoritative source is spec §9 only.

import type { ColorId } from '../../renderer/character/types';
import { COLOR_ID_TO_HEX } from '../../renderer/character/types';

// ─────────────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────────────

/** The 10 shape sprite filename stems (matches assets/textures/shape-icons/*.png). */
export type ShapeIconKey =
  | 'circle'
  | 'triangle'
  | 'square'
  | 'star'
  | 'diamond'
  | 'hexagon'
  | 'heart'
  | 'crescent'
  | 'cross'
  | 'arrow';

// ─────────────────────────────────────────────────────────────────────────────
// Canonical spec §9 mapping (lines 615-630)
// ─────────────────────────────────────────────────────────────────────────────

/** Color → shape sprite key. Spec §9 lines 615-630. */
export const SHAPE_ICON_FOR_COLOR: Record<ColorId, ShapeIconKey> = {
  red: 'circle', // ●
  blue: 'triangle', // ▲
  green: 'square', // ■
  yellow: 'star', // ★
  orange: 'diamond', // ◆
  purple: 'hexagon', // ⬢
  pink: 'heart', // ♥
  cyan: 'crescent', // ◐
  white: 'cross', // ✚
  black: 'arrow', // ▼
};

// ─────────────────────────────────────────────────────────────────────────────
// Helpers — color-lookup utilities
// ─────────────────────────────────────────────────────────────────────────────

/**
 * Reverse-lookup: numeric hex color (0xRRGGBB) → ColorId.
 * Reverses COLOR_ID_TO_HEX from types.ts.
 * Used in game-hud.ts where player.color is stored as a hex number.
 * Returns null for unrecognized values.
 */
export function colorIdFromHex(n: number): ColorId | null {
  for (const [id, hex] of Object.entries(COLOR_ID_TO_HEX) as [ColorId, number][]) {
    if (hex === n) return id;
  }
  return null;
}

/**
 * Index-to-ColorId mapping (0-9).
 * Matches the PLAYER_COLORS array order used in scoreboard.ts and round-summary.ts:
 *   0→red, 1→blue, 2→green, 3→yellow, 4→orange, 5→purple, 6→pink, 7→cyan, 8→white, 9→black
 * Used in scoreboard.ts and round-summary.ts where player.color is stored as an index 0-9.
 * Returns null for out-of-range indices.
 */
const COLOR_INDEX_ORDER: ColorId[] = [
  'red',
  'blue',
  'green',
  'yellow',
  'orange',
  'purple',
  'pink',
  'cyan',
  'white',
  'black',
];

export function colorIdFromIndex(n: number): ColorId | null {
  if (n < 0 || n >= COLOR_INDEX_ORDER.length || !Number.isInteger(n)) return null;
  return COLOR_INDEX_ORDER[n] ?? null;
}

/** Color → unicode glyph (placeholder until Plan 9 wires real sprites).
 *  Mirrors the SHAPE_ICON_FOR_COLOR mapping using spec §9 canonical glyphs.
 *
 *  ⚠️  Do NOT copy from character-select.ts:88-99 — that file uses a different
 *      (pre-spec) ordering that does not match spec §9. */
export const SHAPE_GLYPH_FOR_COLOR: Record<ColorId, string> = {
  red: '●', // circle
  blue: '▲', // triangle
  green: '■', // square
  yellow: '★', // star
  orange: '◆', // diamond
  purple: '⬢', // hexagon
  pink: '♥', // heart
  cyan: '◐', // crescent
  white: '✚', // cross
  black: '▼', // arrow
};
